import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { ActionReducerMap, createFeatureSelector, createReducer, on } from '@ngrx/store';

import { Icons } from '@material/icons/icons.config';
import { AggregatedJobData, AssociationStatusConfiguration, Reason } from '@shared';
import { AssociationLabel } from '@shared/models/association-label.enum';
import { DirectApplicantsPerPhaseNumber } from '@shared/models/direct-applicants-per-phase-number';
import { Pagination, paginationReducer } from '@shared/pagination';

import {
  assignCandidatesClear,
  assignCandidatesList,
  assignCandidatesLoad,
  assignedRejectedCandidatesLoad,
  assignRejectedCandidatesLoadSuccess,
  associateManyJobs,
  associateManyJobsFail,
  associateManyJobsSuccess,
  getJobAssociationStatusConfigurations,
  associationStatusConfigurationsLoadSuccess,
  associateCandidates,
  associateCandidatesSuccess,
} from '../../assigns/store/assigns.actions';
import { hiringManagerCandidatesReducer, HiringManagerCandidatesState } from './hiring-manager';
import {
  jobCandidatesClear,
  jobCandidatesLoadSuccess,
  jobMatchesNumberForJobSuccess,
  loadDirectCandidatesNumberPerPhase,
  loadDirectCandidatesNumberPerPhaseSuccess,
  loadJobMatchesCountsSuccess,
  shortlistedCandidatesWithAssociationsLoadSuccess,
} from './job.actions';
import {
  jobFilterReducer,
  JobFilterState,
} from './matched-candidates-filter/matched-candidates-filter.reducer';

export interface AssociationStatusConfigurationState {
  [jobId: string]: EntityState<AssociationStatusConfiguration>;
}

export const statusConfigurationsAdapter: EntityAdapter<AssociationStatusConfiguration> =
  createEntityAdapter<AssociationStatusConfiguration>({});

export const initialAssociationConfigurationState: AssociationStatusConfigurationState = {};

const initialAssociationConfigurationEntityState = {
  ids: [],
  entities: {},
};

export const DEFAULT_JOB_ID = 'ALL';
export interface RejectedCandidatesState {
  paginationList: Pagination<any>;
  isLoading: boolean;
}

export interface FiltersForShortlist {
  filters: string[];
  hmSelectedStatuses: string[];
}

export const rejectedCandidatesInitialState: RejectedCandidatesState = {
  paginationList: null,
  isLoading: null,
};

export const filtersForShortList: FiltersForShortlist = {
  filters: [],
  hmSelectedStatuses: [],
};

const sortLabels = (a: Reason, b: Reason) => a.label.localeCompare(b.label);

const getIcon = (
  configuration: AssociationStatusConfiguration,
  otherIcon = () => Icons.happy,
): string => (configuration.isRejectedState ? Icons.crying : otherIcon());

export interface JobPageState {
  totalMatchesCandidates: Pagination<any>;
  totalMatchesFilters: JobFilterState;
  shortlistedCandidates: Pagination<any>;
  shortlistedFilters: FiltersForShortlist;
  rejectedCandidates: RejectedCandidatesState;
  assignsCompleted: { fired: number };
  statusConfigurations: AssociationStatusConfigurationState;
  hiringManagerCandidates: HiringManagerCandidatesState;
  matchesNumbers: AggregatedJobData;
  directApplicantsPerPhaseNumber: DirectApplicantsPerPhaseNumber[];
}

export const jobPageReducers: ActionReducerMap<JobPageState> = {
  totalMatchesCandidates: paginationReducer(
    jobCandidatesLoadSuccess.type,
    jobCandidatesClear.type,
    jobCandidatesLoadSuccess.type,
  ),
  totalMatchesFilters: jobFilterReducer,
  shortlistedCandidates: paginationReducer(
    assignCandidatesList.type,
    assignCandidatesClear.type,
    shortlistedCandidatesWithAssociationsLoadSuccess.type,
  ),
  shortlistedFilters: createReducer(
    filtersForShortList,
    on(assignCandidatesLoad, (state, { filters, hmSelectedStatuses }) => ({
      ...state,
      filters,
      hmSelectedStatuses,
    })),
  ),
  rejectedCandidates: createReducer(
    rejectedCandidatesInitialState,
    on(assignedRejectedCandidatesLoad, (state, action) => ({
      ...state,
      isLoading: true,
    })),
    on(assignRejectedCandidatesLoadSuccess, (state, action) => ({
      ...state,
      paginationList: action.payload,
      isLoading: false,
    })),
  ),
  assignsCompleted: createReducer(
    { fired: 0 },
    on(associateManyJobs, associateCandidates, (state, { associations = [] }) => ({
      ...state,
      fired: state.fired + associations.length,
    })),
    on(associateManyJobsSuccess, associateCandidatesSuccess, (state, { associations = [] }) => ({
      ...state,
      fired: state.fired - associations.length,
    })),
    on(associateManyJobsFail, (state, { associationsSize = 0 }) => ({
      ...state,
      fired: state.fired - associationsSize,
    })),
  ),
  statusConfigurations: createReducer(
    initialAssociationConfigurationState,
    on(getJobAssociationStatusConfigurations, (state, { jobId = DEFAULT_JOB_ID }) => ({
      ...state,
      [jobId]: {
        ready: false,
        ...statusConfigurationsAdapter.getInitialState({
          ready: false,
          ...(state[jobId] || initialAssociationConfigurationEntityState),
        }),
      },
    })),
    on(
      associationStatusConfigurationsLoadSuccess,
      (state, { jobId = DEFAULT_JOB_ID, configurations }) => ({
        ...state,
        [jobId]: statusConfigurationsAdapter.upsertMany(
          configurations.map((config, index) => ({
            ...config,
            reasons: config.reasons.sort(sortLabels),
            children: config.children.map((child) => ({
              ...child,
              reasons: child.reasons.sort(sortLabels),
              icon: getIcon(child),
            })),
            icon: getIcon(config, () =>
              index < configurations.length - 1 ? Icons.happy : Icons.superHappy,
            ),
          })),
          {
            ...(state[jobId] || initialAssociationConfigurationEntityState),
            ready: true,
          },
        ),
      }),
    ),
  ),
  hiringManagerCandidates: hiringManagerCandidatesReducer,
  matchesNumbers: createReducer(
    {
      inReviewDirect: 0,
      active: 0,
      passive: 0,
      totalDirect: 0,
      shortlisted: undefined,
      rejected: undefined,
      activeWarm: undefined,
      activeHot: undefined,
      passiveWarm: undefined,
      passiveHot: undefined,
      referrals: 0,
    },
    on(jobMatchesNumberForJobSuccess, (totalMatchesNumbers, { matchesNumbers }) => ({
        ...totalMatchesNumbers,
        ...matchesNumbers,
      })),
    on(loadJobMatchesCountsSuccess, (totalMatchesNumbers, { payload }) => ({
        ...totalMatchesNumbers,
        totalDirect: payload,
      })),
    on(
      jobCandidatesLoadSuccess,
      (totalMatchesNumbers, { payload, category, directCandidatesCount }) => {
        if (!!directCandidatesCount || directCandidatesCount === 0) {
          totalMatchesNumbers = {
            ...totalMatchesNumbers,
            totalDirect: directCandidatesCount,
          };
        }
        if (category === AssociationLabel.PASSIVE) {
          return { ...totalMatchesNumbers, passive: payload.totalElements };
        } else if (category === AssociationLabel.REFERRALS) {
          return { ...totalMatchesNumbers, referrals: payload.totalElements };
        } else {
          return {
            ...totalMatchesNumbers,
            totalDirect: payload.totalElements,
          };
        }
      },
    ),
  ),
  directApplicantsPerPhaseNumber: createReducer(
    [],
    on(loadDirectCandidatesNumberPerPhase, () => []),
    on(
      loadDirectCandidatesNumberPerPhaseSuccess,
      (state, { directApplicantsPerPhaseNumbers }) => directApplicantsPerPhaseNumbers,
    ),
  ),
};

export const getJobPageState = createFeatureSelector<JobPageState>('jobPage');
